/**
 * Pure projectile flight simulation — the single source of truth for what a
 * shot does. Used for the aim preview AND the real flight (playback replays
 * the precomputed points), so the two can never disagree.
 *
 * One call simulates one "segment": it stops at the first terrain hit, player
 * hitbox hit, out-of-bounds, or step limit. Wormhole transit between segments
 * is orchestrated by the caller (see Game.simulateFullFlight in main.ts),
 * identically for the JS and WASM paths.
 *
 * Mirrored in wasm-physics/src/lib.rs (simulate_projectile) — keep in sync.
 */

import type { Vec3, PlanetState, PlayerState, HazardState, ProjectileSimResult } from '../types';
import { PHYS_DT, GEYSER_BOOST_RADIUS, GEYSER_BOOST_VELOCITY, WORMHOLE_COOLDOWN_DIST, MAX_WORMHOLE_TRANSITS, WORMHOLE_SPEED_BOOST, MAX_PROJECTILE_TIME } from '../constants';
import { rk4Step, checkCollision } from './physics';
import { getSurfaceRadius } from './surface';
import { PLAYER_HITBOX_RADIUS } from './physics';

export interface GeyserSim {
  id: number;
  worldPos: Vec3;
  normal: Vec3;
}

export interface HitboxSim {
  playerId: number;
  planetIndex: number;
  center: Vec3;        // 0.5 above the surface point
  surfacePoint: Vec3;  // where the impact is resolved
}

/** Alive geysers as world-space boost sources. */
export function buildGeyserSims(hazards: HazardState[], planets: PlanetState[]): GeyserSim[] {
  const out: GeyserSim[] = [];
  for (const h of hazards) {
    if (!h.alive || h.hazardType !== 'geyser') continue;
    const planet = planets[h.planetIndex];
    if (!planet) continue;
    const d = h.surfaceDir;
    const len = Math.sqrt(d.x * d.x + d.y * d.y + d.z * d.z);
    if (len < 1e-9) continue;
    const nx = d.x / len, ny = d.y / len, nz = d.z / len;
    const r = getSurfaceRadius(planet, d);
    out.push({
      id: h.id,
      worldPos: { x: planet.position.x + nx * r, y: planet.position.y + ny * r, z: planet.position.z + nz * r },
      normal: { x: nx, y: ny, z: nz },
    });
  }
  return out;
}

/** Hitboxes of every player a projectile can hit (skips owner, dead, airborne). */
export function buildHitboxes(players: PlayerState[], planets: PlanetState[], ownerId: number): HitboxSim[] {
  const out: HitboxSim[] = [];
  for (const p of players) {
    if (!p.alive || p.airborne || p.id === ownerId) continue;
    const planet = planets[p.planetIndex];
    if (!planet || !planet.alive) continue;
    const d = p.surfaceDir;
    const len = Math.sqrt(d.x * d.x + d.y * d.y + d.z * d.z);
    if (len < 1e-3) continue;
    const nx = d.x / len, ny = d.y / len, nz = d.z / len;
    const r = getSurfaceRadius(planet, d);
    const sx = planet.position.x + nx * r, sy = planet.position.y + ny * r, sz = planet.position.z + nz * r;
    out.push({
      playerId: p.id,
      planetIndex: p.planetIndex,
      surfacePoint: { x: sx, y: sy, z: sz },
      center: { x: sx + nx * 0.5, y: sy + ny * 0.5, z: sz + nz * 0.5 },
    });
  }
  return out;
}

/**
 * Simulate one flight segment at the fixed physics timestep.
 *
 * @param cooldownPlanetIndex planet index of the wormhole the projectile just
 *   exited (collision with it is ignored until the projectile is
 *   WORMHOLE_COOLDOWN_DIST radii away), or -1.
 */
export function simulateFlightSegment(
  startPos: Vec3,
  startVel: Vec3,
  planets: PlanetState[],
  geysers: GeyserSim[],
  hitboxes: HitboxSim[],
  maxSteps: number,
  outOfBounds: number,
  cooldownPlanetIndex = -1,
): ProjectileSimResult {
  const points: Vec3[] = [{ ...startPos }];
  let pos = { ...startPos };
  let vel = { ...startVel };
  const boosted = new Set<number>();
  const geyserEvents: { step: number; geyserId: number }[] = [];
  const hitR2 = PLAYER_HITBOX_RADIUS * PLAYER_HITBOX_RADIUS;
  let cooldown = cooldownPlanetIndex;

  let hitPlanetIndex = -1;
  let hitPoint: Vec3 | null = null;
  let hitPlayerId = -1;

  for (let step = 0; step < maxSteps; step++) {
    const r = rk4Step(pos, vel, PHYS_DT, planets);
    pos = r.pos;
    vel = r.vel;
    points.push(pos);

    // Geyser boosts (each geyser once per segment)
    for (const g of geysers) {
      if (boosted.has(g.id)) continue;
      const gx = pos.x - g.worldPos.x, gy = pos.y - g.worldPos.y, gz = pos.z - g.worldPos.z;
      if (Math.sqrt(gx * gx + gy * gy + gz * gz) < GEYSER_BOOST_RADIUS) {
        vel = { x: vel.x + g.normal.x * GEYSER_BOOST_VELOCITY, y: vel.y + g.normal.y * GEYSER_BOOST_VELOCITY, z: vel.z + g.normal.z * GEYSER_BOOST_VELOCITY };
        boosted.add(g.id);
        geyserEvents.push({ step: step + 1, geyserId: g.id });
      }
    }

    // Player hitboxes
    for (const hb of hitboxes) {
      const hx = pos.x - hb.center.x, hy = pos.y - hb.center.y, hz = pos.z - hb.center.z;
      if (hx * hx + hy * hy + hz * hz <= hitR2) {
        hitPlayerId = hb.playerId;
        hitPlanetIndex = hb.planetIndex;
        hitPoint = { ...hb.surfacePoint };
        break;
      }
    }
    if (hitPlayerId >= 0) break;

    // Wormhole cooldown clears once far enough from the exit wormhole
    if (cooldown >= 0) {
      const w = planets[cooldown];
      const wx = pos.x - w.position.x, wy = pos.y - w.position.y, wz = pos.z - w.position.z;
      if (Math.sqrt(wx * wx + wy * wy + wz * wz) > w.radius * WORMHOLE_COOLDOWN_DIST) cooldown = -1;
    }

    // Terrain
    const c = checkCollision(pos, planets, cooldown);
    if (c) {
      hitPlanetIndex = c.planetIndex;
      hitPoint = c.point;
      break;
    }

    // Out of bounds
    if (Math.sqrt(pos.x * pos.x + pos.y * pos.y + pos.z * pos.z) > outOfBounds) break;
  }

  return { points, hitPlanetIndex, hitPoint, finalVel: vel, geyserEvents, hitPlayerId };
}

// =============================================================================
// Full flight (multi-segment: wormhole transits)
// =============================================================================

export type SegmentSimulator = (
  pos: Vec3, vel: Vec3, planets: PlanetState[], geysers: GeyserSim[], hitboxes: HitboxSim[],
  maxSteps: number, outOfBounds: number, cooldownPlanetIndex: number,
) => ProjectileSimResult;

/**
 * Simulate a projectile's complete flight, chaining segments through wormhole
 * transits (exit at the linked wormhole with WORMHOLE_SPEED_BOOST, cooldown on
 * the exit wormhole, destroyed after MAX_WORMHOLE_TRANSITS). Stars and
 * planets end the flight; so does a player hitbox.
 *
 * `segment` defaults to the pure JS simulator; the game injects a WASM-backed
 * one that produces identical results.
 */
export function simulateFlight(
  startPos: Vec3,
  startVel: Vec3,
  planets: PlanetState[],
  geysers: GeyserSim[],
  hitboxes: HitboxSim[],
  outOfBounds: number,
  segment: SegmentSimulator = simulateFlightSegment,
  maxStepsTotal: number = Math.ceil(MAX_PROJECTILE_TIME / PHYS_DT),
): ProjectileSimResult {
  let pos = { ...startPos };
  let vel = { ...startVel };
  const allPoints: Vec3[] = [];
  const allGeyserEvents: { step: number; geyserId: number }[] = [];
  let finalHitPlanetIndex = -1;
  let finalHitPoint: Vec3 | null = null;
  let finalVel = { ...vel };
  let hitPlayerId = -1;
  let wormholeTransits = 0;
  let cooldownIdx = -1;
  let totalStepsUsed = 0;

  for (let seg = 0; seg < MAX_WORMHOLE_TRANSITS + 1; seg++) {
    const remainingSteps = maxStepsTotal - totalStepsUsed;
    if (remainingSteps <= 0) break;

    const sim = segment(pos, vel, planets, geysers, hitboxes, remainingSteps, outOfBounds, cooldownIdx);

    for (const ge of sim.geyserEvents) allGeyserEvents.push({ step: ge.step + totalStepsUsed, geyserId: ge.geyserId });
    const startIdx = seg > 0 ? 1 : 0; // skip the duplicated segment start point
    for (let i = startIdx; i < sim.points.length; i++) allPoints.push(sim.points[i]);
    totalStepsUsed += sim.points.length - 1;
    finalVel = sim.finalVel;

    if (sim.hitPlayerId >= 0) {
      hitPlayerId = sim.hitPlayerId;
      finalHitPlanetIndex = sim.hitPlanetIndex;
      finalHitPoint = sim.hitPoint;
      break;
    }
    if (sim.hitPlanetIndex < 0) break; // out of bounds or timeout

    const hit = planets[sim.hitPlanetIndex];
    if (hit.type === 'wormhole') {
      wormholeTransits++;
      const linked = hit.linkedWormholeId != null ? planets.findIndex(p => p.id === hit.linkedWormholeId) : -1;
      if (wormholeTransits >= MAX_WORMHOLE_TRANSITS || linked < 0) {
        finalHitPlanetIndex = sim.hitPlanetIndex;
        finalHitPoint = sim.hitPoint;
        break;
      }
      const exit = planets[linked];
      const speed = Math.sqrt((sim.finalVel.x * sim.finalVel.x) + (sim.finalVel.y * sim.finalVel.y) + (sim.finalVel.z * sim.finalVel.z));
      const dir = speed > 0.001
        ? { x: sim.finalVel.x / speed, y: sim.finalVel.y / speed, z: sim.finalVel.z / speed }
        : { x: 1, y: 0, z: 0 };
      pos = { x: exit.position.x + dir.x * exit.radius * 1.5, y: exit.position.y + dir.y * exit.radius * 1.5, z: exit.position.z + dir.z * exit.radius * 1.5 };
      vel = { x: dir.x * speed * WORMHOLE_SPEED_BOOST, y: dir.y * speed * WORMHOLE_SPEED_BOOST, z: dir.z * speed * WORMHOLE_SPEED_BOOST };
      cooldownIdx = linked;
      continue;
    }

    finalHitPlanetIndex = sim.hitPlanetIndex; // star or regular planet
    finalHitPoint = sim.hitPoint;
    break;
  }

  return { points: allPoints, hitPlanetIndex: finalHitPlanetIndex, hitPoint: finalHitPoint, finalVel, geyserEvents: allGeyserEvents, hitPlayerId };
}
